import type { MeetingSession } from 'amazon-chime-sdk-js';
import { useCallback, useEffect } from 'react';

import { useSourceMediaTracksContext } from './useSourceMediaTracksContext';

import { useAppSelector } from 'features/app/hooks';
import { useMercuryVideoResolution } from 'features/media-streams/hooks';
import { mediaDevicesSelector } from 'features/media-devices/store';
import type { RtcConnection } from 'features/sip/interfaces';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';

export const useSourceMediaTracks = () => {
  const { service } = useSourceMediaTracksContext();
  const videoSettings = useMercuryVideoResolution();

  const { camera, microphone } = useAppSelector(mediaDevicesSelector);

  useEffect(() => {
    service.setMicrophoneId(microphone);
    service.setCameraId(camera);
    service.setVideoSettings(videoSettings);
  }, [service, microphone, camera, videoSettings]);

  const getSourceMediaTrack = useCallback(
    (mediaType: SourceMediaTypes) => {
      return service.getTrack(mediaType);
    },
    [service]
  );

  const isSourceMediaTrackAvailable = useCallback(
    (mediaType: SourceMediaTypes) => {
      return service.isTrackAvailable(mediaType);
    },
    [service]
  );

  const setDeafRtcConnection = useCallback(
    (rtcConnection: RtcConnection | undefined) => {
      return service.setDeafRtcConnection(rtcConnection);
    },
    [service]
  );

  const setHearingAudioTrack = useCallback(
    async (audioStream: MediaStream) => {
      const audioTracks = audioStream.getAudioTracks();
      if (audioTracks.length === 0) {
        console.warn(
          'DEBUG: No audio track found in meetingSession audio stream!'
        );
        return;
      }
      if (audioTracks.length > 1) {
        console.warn(
          'DEBUG: Multiple audio tracks found in meetingSession audio stream - using the first one'
        );
      }
      service.setRemoteTrack(SourceMediaTypes.HEARING_AUDIO, audioTracks[0]);
    },
    [service]
  );

  const setHearingVoiceMeetingSession = useCallback(
    async (meetingSession: MeetingSession | undefined) => {
      service.setRemoteTrack(SourceMediaTypes.HEARING_AUDIO, undefined);

      if (!meetingSession) {
        return;
      }

      // Determine if we're currently monitoring this meeting session
      if (
        service.getUserData(SourceMediaTypes.HEARING_AUDIO) ===
        meetingSession.audioVideo
      ) {
        console.log('DEBUG: Already monitoring this meeting session');
        return;
      }

      service.setUserData(
        SourceMediaTypes.HEARING_AUDIO,
        meetingSession.audioVideo
      );

      // First, attempt to get the current audio track from the meeting session
      const audioStream =
        await meetingSession.audioVideo.getCurrentMeetingAudioStream();
      if (audioStream) {
        setHearingAudioTrack(audioStream);
      }

      // Monitor the meeting session for changes to the audio stream
      meetingSession.audioVideo.addAudioMixObserver({
        meetingAudioStreamBecameActive: (activeStream) => {
          setHearingAudioTrack(activeStream);
        },
        meetingAudioStreamBecameInactive: (_) => {
          service.setRemoteTrack(SourceMediaTypes.HEARING_AUDIO, undefined);
        },
      });
    },
    [service, setHearingAudioTrack]
  );

  const addStreamsChangedListener = useCallback(
    (listener: EventListenerOrEventListenerObject) => {
      service.addStreamsChangedListener(listener);
    },
    [service]
  );

  const removeStreamsChangedListener = useCallback(
    (listener: EventListenerOrEventListenerObject) => {
      service.removeStreamsChangedListener(listener);
    },
    [service]
  );

  const getActiveTracks = useCallback(
    () => service.getActiveTracks(),
    [service]
  );

  return {
    getSourceMediaTrack,
    isSourceMediaTrackAvailable,
    setDeafRtcConnection,
    setHearingVoiceMeetingSession,
    getActiveTracks,
    addStreamsChangedListener,
    removeStreamsChangedListener,
  };
};
